import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const links = [
  { label: "Azienda", href: "#manifesto" },
  { label: "Storia", href: "#storia" },
  { label: "Parco Macchine", href: "#tecnologia" },
  { label: "Lavorazioni", href: "#lavorazioni" },
  { label: "Sedi", href: "#sedi" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Navigate to a section. Uses Lenis for smooth scroll when available,
  // otherwise falls back to the native anchor jump (scroll-margin-top in CSS
  // keeps the target below the fixed header).
  const handleNav = (e, href) => {
    setOpen(false);
    const target = document.querySelector(href);
    if (target && window.__lenis) {
      e.preventDefault();
      window.__lenis.scrollTo(target, { offset: -80, duration: 1.1 });
    }
  };

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.19, 1, 0.22, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-colors duration-500 ${
        scrolled ? "backdrop-blur-xl bg-zinc-950/80 border-white/10" : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <a href="#top" data-testid="logo" onClick={(e) => handleNav(e, "#top")} className="flex items-center gap-3 group">
          <img
            src="/cobi-logo-white.png"
            alt="Logo Cobi Meccanica"
            className="w-9 h-9 object-contain group-hover:scale-105 transition-transform duration-500"
          />
          <span className="font-display text-lg uppercase tracking-[0.28em] leading-none whitespace-nowrap">
            <span className="font-bold">Cobi</span>
            <span className="font-light text-zinc-300 ml-1.5">Meccanica</span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.href.slice(1)}`}
              onClick={(e) => handleNav(e, l.href)}
              className="hover-underline text-sm uppercase tracking-widest text-zinc-300 hover:text-white transition-colors py-1"
            >
              {l.label}
            </a>
          ))}
          <a
            href="#contatti"
            data-testid="nav-contact"
            onClick={(e) => handleNav(e, "#contatti")}
            className="text-sm uppercase tracking-widest border border-white/20 px-5 py-2.5 hover:bg-red-600 hover:border-red-600 hover:text-white transition-colors"
          >
            Contattaci
          </a>
        </nav>

        <button data-testid="menu-toggle" aria-label="Menu" className="md:hidden text-white" onClick={() => setOpen(!open)}>
          {open ? <X /> : <Menu />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="md:hidden overflow-hidden bg-zinc-950 border-t border-white/10"
          >
            <div className="px-6 py-6 flex flex-col">
              {[...links, { label: "Contattaci", href: "#contatti" }].map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`mobile-nav-${l.href.slice(1)}`}
                  onClick={(e) => handleNav(e, l.href)}
                  className="block py-4 text-lg uppercase tracking-wide text-zinc-200 border-b border-white/10 last:border-b-0 active:text-red-600"
                >
                  {l.label}
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
